import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import API, { formatApiError } from "@/lib/api";
import { CONTENT_SCHEMA } from "@/lib/contentSchema";
import { useContent } from "@/context/ContentProvider";

const inputCls = "mt-1.5 w-full rounded-xl border border-[#4b3a2f]/15 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#c9a24b]";

const ImageField = ({ value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await API.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <div className="mt-1.5 flex items-center gap-3">
        {value ? (
          <img src={value} alt="" className="h-14 w-14 rounded-xl border border-[#e8ded4] object-cover" />
        ) : (
          <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#f2e9dd] text-xs text-[#a47b52]">N/D</span>
        )}
        <label className="cursor-pointer rounded-full border border-[#4b3a2f]/20 px-4 py-2 text-xs font-semibold text-[#6b5b52] hover:bg-[#f2e9dd]">
          {busy ? "Subiendo…" : "Subir archivo"}
          <input type="file" accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt" className="hidden" onChange={upload} />
        </label>
        {value && <button type="button" onClick={() => onChange("")} className="text-xs text-[#c85a48] hover:underline">Quitar</button>}
      </div>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://… o sube un archivo" className={inputCls} />
    </div>
  );
};

const ListStringField = ({ value, onChange }) => {
  const list = Array.isArray(value) ? value : [];
  return (
    <div className="space-y-2">
      {list.map((item, i) => (
        <div key={i} className="flex gap-2">
          <input value={item} onChange={(e) => { const n = [...list]; n[i] = e.target.value; onChange(n); }} className={inputCls} />
          <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="mt-1.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#c85a48]/30 text-[#c85a48] hover:bg-[#c85a48] hover:text-white">
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...list, ""])} className="inline-flex items-center gap-1.5 rounded-full bg-[#f2e9dd] px-4 py-2 text-xs font-semibold text-[#6b4e3d]">
        <Plus size={13} /> Agregar
      </button>
    </div>
  );
};

const ListGroupedField = ({ value, onChange, subfields }) => {
  const list = Array.isArray(value) ? value : [];
  const set = (i, k, v) => { const n = [...list]; n[i] = { ...n[i], [k]: v }; onChange(n); };
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const n = [...list];
    [n[i], n[j]] = [n[j], n[i]];
    onChange(n);
  };
  return (
    <div className="space-y-4">
      {list.map((item, i) => (
        <div key={i} className="rounded-2xl border border-[#e8ded4] bg-[#faf7f2] p-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="font-mono-d text-[10px] uppercase tracking-widest text-[#a47b52]">Elemento {i + 1}</p>
            <div className="flex gap-1">
              <button type="button" onClick={() => move(i, -1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-[#4b3a2f]/15 text-[#6b5b52]"><ArrowUp size={12} /></button>
              <button type="button" onClick={() => move(i, 1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-[#4b3a2f]/15 text-[#6b5b52]"><ArrowDown size={12} /></button>
              <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="flex h-7 w-7 items-center justify-center rounded-full border border-[#c85a48]/30 text-[#c85a48] hover:bg-[#c85a48] hover:text-white"><Trash2 size={12} /></button>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {subfields.map((f) => (
              <label key={f.key} className={`block ${f.type === "textarea" || f.type === "image" ? "sm:col-span-2" : ""}`}>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#a47b52]">{f.label}</span>
                <FieldInput type={f.type} value={item[f.key]} onChange={(v) => set(i, f.key, v)} />
              </label>
            ))}
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...list, Object.fromEntries(subfields.map((f) => [f.key, ""]))])} className="inline-flex items-center gap-1.5 rounded-full bg-[#f2e9dd] px-4 py-2 text-xs font-semibold text-[#6b4e3d]">
        <Plus size={13} /> Agregar elemento
      </button>
    </div>
  );
};

const FieldInput = ({ type, value, onChange }) => {
  if (type === "textarea") return <textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
  if (type === "image") return <ImageField value={value} onChange={onChange} />;
  return <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
};

const ContentEditor = () => {
  const { content, reload } = useContent();
  const [groupId, setGroupId] = useState(CONTENT_SCHEMA[0].id);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft({ ...content }); }, [content]);

  const group = useMemo(() => CONTENT_SCHEMA.find((g) => g.id === groupId), [groupId]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(content);

  const save = async () => {
    setSaving(true);
    try {
      await API.put("/admin/content", { updates: draft });
      toast.success("Contenido actualizado — ya está visible en el sitio");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[240px_1fr]" data-testid="content-editor">
      <aside className="h-fit rounded-2xl border border-[#e8ded4] bg-white p-3">
        {CONTENT_SCHEMA.map((g) => (
          <button
            key={g.id}
            onClick={() => setGroupId(g.id)}
            data-testid={`content-group-${g.id}`}
            className={`mb-1 w-full rounded-xl px-4 py-2.5 text-left text-sm transition-colors ${groupId === g.id ? "bg-[#4b3a2f] text-[#f2e9dd]" : "text-[#6b5b52] hover:bg-[#f2e9dd]"}`}
          >
            {g.label}
          </button>
        ))}
      </aside>

      <div className="rounded-2xl border border-[#e8ded4] bg-white p-6 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h3 className="font-serif-d text-3xl font-semibold text-[#4b3a2f]">{group.label}</h3>
          <button
            onClick={save}
            disabled={saving || !dirty}
            data-testid="content-save-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#c9a24b] px-6 py-3 text-sm font-semibold text-[#4b3a2f] transition-all hover:bg-[#b8913b] disabled:opacity-40"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Guardar cambios
          </button>
        </div>

        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          {group.fields.map((f) => (
            <label key={f.key} className={`block ${["textarea", "image", "list_string", "list_grouped", "bool"].includes(f.type) ? "sm:col-span-2" : ""}`}>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#a47b52]">{f.label}</span>
              {f.type === "bool" ? (
                <label className="mt-2 flex items-center gap-2 text-sm text-[#4b3a2f]">
                  <input
                    type="checkbox"
                    checked={!!draft[f.key]}
                    onChange={(e) => setDraft({ ...draft, [f.key]: e.target.checked })}
                    className="h-4 w-4 accent-[#c9a24b]"
                    data-testid={`content-field-${f.key}`}
                  />
                  Activado
                </label>
              ) : f.type === "list_string" ? (
                <ListStringField value={draft[f.key]} onChange={(v) => setDraft({ ...draft, [f.key]: v })} />
              ) : f.type === "list_grouped" ? (
                <ListGroupedField value={draft[f.key]} onChange={(v) => setDraft({ ...draft, [f.key]: v })} subfields={f.fields} />
              ) : (
                <FieldInput type={f.type} value={draft[f.key]} onChange={(v) => setDraft({ ...draft, [f.key]: v })} />
              )}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContentEditor;
